import {
  Component,
  computed,
  effect,
  input,
  OnInit,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FieldTree } from '@angular/forms/signals';
import { InputNumberModule } from 'primeng/inputnumber';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'app-price-setting',
  imports: [FormsModule, InputNumberModule, FloatLabelModule, ButtonModule],
  templateUrl: './price-setting.component.html',
  styleUrl: './price-setting.component.scss',
})
export class PriceSettingComponent implements OnInit {
  costField = input.required<FieldTree<number>>();
  markupField = input.required<FieldTree<number>>();
  markupPercentField = input<FieldTree<number>>();
  priceField = input<FieldTree<number>>();

  costLabel = input('Cost');
  markupLabel = input('Markup');
  priceLabel = input('Price');
  currencyCode = input.required<string>();

  private initialCost = 0;
  private initialMarkup = 0;
  private initialMarkupPercent = 0;
  private initialPrice = 0;

  isDirty = computed(
    () =>
      this.costField()().dirty() ||
      this.markupField()().dirty() ||
      (this.markupPercentField()?.().dirty() ?? false) ||
      (this.priceField()?.().dirty() ?? false),
  );

  constructor() {
    effect(() => {
      const costState = this.costField()();
      costState.value();
      if (!costState.dirty()) {
        return;
      }
      untracked(() => this.updateFromCost());
    });

    effect(() => {
      const markupState = this.markupField()();
      markupState.value();
      if (!markupState.dirty()) {
        return;
      }
      untracked(() => this.updateFromMarkup());
    });

    effect(() => {
      const markupPercentField = this.markupPercentField();
      if (!markupPercentField) {
        return;
      }
      const markupPercentState = markupPercentField();
      markupPercentState.value();
      if (!markupPercentState.dirty()) {
        return;
      }
      untracked(() => this.updateFromMarkupPercent());
    });

    effect(() => {
      const priceField = this.priceField();
      if (!priceField) {
        return;
      }
      const priceState = priceField();
      priceState.value();
      if (!priceState.dirty()) {
        return;
      }
      untracked(() => this.updateFromPrice());
    });
  }

  ngOnInit() {
    this.captureInitialValues();
  }

  private captureInitialValues() {
    this.initialCost = this.costField()().value();
    this.initialMarkup = this.markupField()().value();
    this.initialMarkupPercent = this.markupPercentField()?.().value() ?? 0;
    this.initialPrice = this.priceField()?.().value() ?? 0;
  }

  private setValueIfChanged(
    field: FieldTree<number> | undefined,
    value: number,
  ): void {
    if (!field || field().value() === value) {
      return;
    }
    field().value.set(value);
  }

  private updateFromCost() {
    const cost = this.costField()().value();
    const markupPercent = this.markupPercentField()?.().value() ?? 0;

    const markup = Math.round(cost * markupPercent) / 100;
    this.setValueIfChanged(this.markupField(), markup);

    const unitPrice = cost + markup;
    this.setValueIfChanged(this.priceField(), unitPrice);
  }

  private updateFromMarkup() {
    const cost = this.costField()().value();
    const markup = this.markupField()().value();

    const markupPercent =
      cost !== 0 ? Math.round((markup / cost) * 10000) / 100 : 0;
    this.setValueIfChanged(this.markupPercentField(), markupPercent);

    const unitPrice = cost + markup;
    this.setValueIfChanged(this.priceField(), unitPrice);
  }

  private updateFromMarkupPercent() {
    const cost = this.costField()().value();
    const markupPercent = this.markupPercentField()!().value();

    const markup = cost * (markupPercent / 100);
    this.setValueIfChanged(this.markupField(), markup);

    const unitPrice = cost + markup;
    this.setValueIfChanged(this.priceField(), unitPrice);
  }

  private updateFromPrice() {
    const unitPrice = this.priceField()!().value();
    const markupPercent = this.markupPercentField()?.().value() ?? 0;

    const denominator = 1 + markupPercent / 100;
    const cost = denominator !== 0 ? unitPrice / denominator : 0;
    const markup = unitPrice - cost;

    this.setValueIfChanged(this.costField(), cost);
    this.setValueIfChanged(this.markupField(), markup);
  }

  resetPricing() {
    this.costField()().reset(this.initialCost);
    this.markupField()().reset(this.initialMarkup);
    this.markupPercentField()?.().reset(this.initialMarkupPercent);
    this.priceField()?.().reset(this.initialPrice);
  }

  protected readonly Math = Math;

  getCurrencyInputWidth(value: number | null | undefined): string {
    const numValue = value ?? 0;
    const formatted = Number(numValue).toFixed(2);
    return `calc(${Math.max(1, formatted.length)}ch + 2em)`;
  }

  setControlValue(
    field: FieldTree<unknown, string | number>,
    value: unknown,
  ): void {
    field().controlValue.set(value as never);
  }

  markTouched(field: FieldTree<unknown, string | number>): void {
    field().markAsTouched();
  }
}
